import React, { FC, useCallback } from 'react'
import Flatpickr from 'react-flatpickr'
import moment from 'moment'
import { Button } from 'transfix-ui/components/Button'
import { ButtonGroup } from 'transfix-ui/components/ButtonGroup'
import { FormattedDateTimeRange } from 'transfix-ui/components/FormattedDateTimeRange'
import { IDateRange } from 'apps/tms/components/Insights/types'
import { DATE_RANGE_OPTIONS_SHORT } from 'apps/tms/constants/filters'
import { FLATPICKR_DEFAULTS } from 'constants/formElements'

interface IDateRangeFilterProps {
  value: IDateRange
  onChange: (value: IDateRange) => void
}

export const DateRangeFilter: FC<IDateRangeFilterProps> = props => {
  const {
    onChange,
    value: { dateFilter, endAt, startAt },
  } = props

  const isCustomDate = dateFilter === 'custom_date'

  const buttonGroupValueHandler = useCallback(
    value => {
      const customDateFilterFieldsReset =
        value !== 'custom_date' ? { startAt: undefined, endAt: undefined } : {}

      onChange({
        dateFilter: value,
        // Clears the startAt and endAt filters fields whenever a new date range filter is
        // elected. Otherwise, no start and end date is necessary for the
        // request.
        ...customDateFilterFieldsReset,
      })
    },
    [onChange]
  )

  const datesHandler = useCallback(
    (dates: Date[]) => {
      const [start, end] = dates || []
      if (start && end) {
        onChange({
          dateFilter: 'custom_date',
          startAt: start.toISOString(),
          endAt: end.toISOString(),
        })
      }
    },
    [onChange]
  )

  const flatPickrRender = useCallback(
    ({ value }, ref) => {
      const [flatPickrStart, flatPickrEnd] = (value ?? []) as [string, string]

      const buttonText =
        flatPickrStart && flatPickrEnd ? (
          <FormattedDateTimeRange
            as='div'
            endAt={flatPickrEnd}
            startAt={flatPickrStart}
            endAtTimezone={moment.tz.guess()}
            startAtTimezone={moment.tz.guess()}
            format='dateAndYear'
            abbreviated
          />
        ) : (
          'Custom'
        )

      return (
        <Button
          ref={ref as React.Ref<HTMLButtonElement>}
          color={isCustomDate ? 'primary' : 'default'}
          value='custom_date'
          selected={isCustomDate}
          data-test='Transfix-Shipper-App-TimeFrameSelect'>
          {buttonText}
        </Button>
      )
    },
    [isCustomDate]
  )

  return (
    <ButtonGroup inline hideLabel onChange={buttonGroupValueHandler} value={dateFilter}>
      {/* Using a literal array to pass children otherwise ButtonGroup children type definition will complain */}
      {[
        ...DATE_RANGE_OPTIONS_SHORT.map(({ label, value }) => (
          <Button key={value} value={value} data-test='Transfix-Shipper-App-TimeFrameSelect'>
            {label}
          </Button>
        )),
        <Flatpickr
          key='Flatpickr'
          render={flatPickrRender}
          options={{
            ...FLATPICKR_DEFAULTS,
            mode: 'range',
            showMonths: 2,
          }}
          onChange={datesHandler}
          value={startAt && endAt ? [new Date(startAt), new Date(endAt)] : undefined}
        />,
      ]}
    </ButtonGroup>
  )
}
